import { useGetPrivacyPolicies, useUpdatePrivacyPolicies } from '@/hooks/api';
import type { AppInfoWithPrivacyFlags, PrivacyFlag } from '@/portal-api.definition';
import { useI18n, Radio, cn } from '@nosinovacao/nosid-mfe-common';
import type { StepNavigation } from './models';

export function SettingsConsentsSection({ goToStep }: Readonly<StepNavigation>) {
  const { data } = useGetPrivacyPolicies();

  return (
    <div className="flex max-h-[calc(100dvh-130px)] flex-col gap-12 overflow-y-auto pb-2 tablet:max-h-[calc(100dvh-180px)]">
      {data?.map((policy) => (
        <div key={policy.Client.Id} className="grid gap-2">
          <h3 className="font-bold text-nos-neutral-900 font-title-h4">{policy.Name}</h3>
          <div className="grid gap-6">
            {policy.PrivacyFlags.map((flag) => (
              <ConsentItem
                key={`${policy.Client.Id}-${flag.Type}`}
                flag={flag}
                policy={policy}
                onShowDetail={(consent) => {
                  goToStep('consent-detail', {
                    title: policy.Name,
                    consent,
                  });
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConsentItem({
  flag,
  policy,
  onShowDetail,
}: Readonly<{
  flag: PrivacyFlag;
  policy: AppInfoWithPrivacyFlags;
  onShowDetail: (consent: PrivacyFlag) => void;
}>) {
  const { t } = useI18n();
  const { mutate, isPending } = useUpdatePrivacyPolicies();

  const handleToggle = (isAccepted: boolean) => {
    mutate({
      ClientId: policy.Client.Id,
      PrivacyFlags: [
        {
          IsAccepted: isAccepted,
          Type: flag.Type,
          TextCategory: flag.TextCategory,
        },
      ],
    });
  };

  return (
    <div className="grid gap-6">
      <span className="font-normal text-nos-neutral-700 font-text-p2">
        <span className="inline">{t('Common.Consents.Prefix')} </span>
        <button
          type="button"
          data-tid="button-consent-detail"
          onClick={() => onShowDetail(flag)}
          className="cursor-pointer font-medium underline"
        >
          {flag.Title}
        </button>
      </span>
      <div className="flex items-center justify-center gap-6">
        <div className="flex cursor-pointer items-center gap-2">
          <Radio
            skin="light"
            checked={flag.IsAccepted}
            onChange={() => handleToggle(true)}
            disabled={isPending}
            data-tid="radio-consent-accepted"
          />
          <span
            className={cn(
              flag.IsAccepted ? 'font-medium' : 'font-normal',
              'text-nos-neutral-900 font-ui-9',
            )}
          >
            {t('Common.Consents.Accepted')}
          </span>
        </div>
        <div className="flex cursor-pointer items-center gap-2">
          <Radio
            skin="light"
            checked={!flag.IsAccepted}
            onChange={() => handleToggle(false)}
            disabled={isPending || flag.IsMandatory}
            data-tid="radio-consent-not-accepted"
          />
          <span
            className={cn(
              !flag.IsAccepted ? 'font-medium' : 'font-normal',
              'text-nos-neutral-900 font-ui-9',
            )}
          >
            {t('Common.Consents.NotAccepted')}
          </span>
        </div>
      </div>
    </div>
  );
}

interface ConsentDetailParams {
  title: string;
  consent: PrivacyFlag;
}

export function SettingsConsentDetailSection({
  params,
}: Readonly<StepNavigation<never, ConsentDetailParams>>) {
  if (!params?.consent) return null;

  return (
    <div className="max-h-[calc(100dvh-130px)] overflow-y-auto tablet:max-h-[calc(100dvh-180px)]">
      <p className="text-nos-neutral-900 font-text-p2">{params.consent.Message}</p>
    </div>
  );
}
